import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { verifyEmail, resendOtp } from "../api/verification.js";
import "../css/VerifyEmail.css";

const CODE_LENGTH = 5; // keep in sync with OTP_CODE_LENGTH on backend
const RESEND_COOLDOWN_SECONDS = 60; // keep in sync with OTP_RESEND_COOLDOWN_SECONDS on backend

function extractErrorMessage(error) {
  const data = error?.response?.data;
  if (!data) return "Something went wrong. Please try again.";
  if (typeof data === "string") return data;
  if (data.detail) return data.detail;
  if (data.non_field_errors) return data.non_field_errors[0];
  const firstKey = Object.keys(data)[0];
  if (firstKey && Array.isArray(data[firstKey])) return data[firstKey][0];
  return "Something went wrong. Please try again.";
}

export default function VerifyEmail() {
  const navigate = useNavigate();
  const location = useLocation();
  const { lang: urlLang } = useParams();
  const email = location.state?.email; 

  const [lang, setLang] = useState(urlLang === "fa" || urlLang === "en" ? urlLang : "fa");
  const [digits, setDigits] = useState(Array(CODE_LENGTH).fill(""));
  const [error, setError] = useState("");
  const [shake, setShake] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_SECONDS);
  const [verified, setVerified] = useState(false);

  const timerRef = useRef(null);
  const inputRefs = useRef([]);
  const hasAutoSubmitted = useRef(false);

  const from = location.state?.from?.pathname || `/${lang}/profile`;

  // Detect language once, from URL param or device timezone as fallback
  useEffect(() => {
    if (urlLang === "fa" || urlLang === "en") {
      setLang(urlLang);
      return;
    }
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    setLang(userTimezone === "Asia/Tehran" ? "fa" : "en");
  }, [urlLang]);

  useEffect(() => {
    if (!email) {
      navigate(`/${lang}/register`, { replace: true }); // landed here directly, no phone in state
      return;
    }
    startCooldown();
    inputRefs.current[0]?.focus();
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function startCooldown() {
    setCooldown(RESEND_COOLDOWN_SECONDS);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }

  function updateDigit(index, value) {
    setDigits((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  }

  function handleDigitChange(index, rawValue) {
    const value = rawValue.replace(/\D/g, "");
    if (!value) {
      updateDigit(index, "");
      return;
    }
    // Also handles a code pasted/autofilled into a single box
    const chars = value.split("");
    setDigits((prev) => {
      const next = [...prev];
      let cursor = index;
      for (const ch of chars) {
        if (cursor >= CODE_LENGTH) break;
        next[cursor] = ch;
        cursor += 1;
      }
      const focusIndex = Math.min(cursor, CODE_LENGTH - 1);
      inputRefs.current[focusIndex]?.focus();
      return next;
    });
    setError("");
  }

  function handleKeyDown(index, e) {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
      updateDigit(index - 1, "");
    } else if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  }

  function handlePaste(e) {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, CODE_LENGTH);
    if (!pasted) return;
    const next = Array(CODE_LENGTH).fill("");
    pasted.split("").forEach((ch, i) => { next[i] = ch; });
    setDigits(next);
    setError("");
    inputRefs.current[Math.min(pasted.length, CODE_LENGTH - 1)]?.focus();
  }

  async function submitCode(code) {
    setIsSubmitting(true);
    setError("");
    try {
      const { data } = await verifyEmail({ email, code });
      localStorage.setItem("authToken", data.token);
      setVerified(true);
      setTimeout(() => navigate(from, { replace: true }), 1200);
    } catch (err) {
      setError("Something went wrong. Please try again.");
      setShake(true);
      setTimeout(() => setShake(false), 400);
      setDigits(Array(CODE_LENGTH).fill(""));
      hasAutoSubmitted.current = false;
      inputRefs.current[0]?.focus();
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleSubmit(e) {
    e.preventDefault();
    const code = digits.join("");
    if (code.length !== CODE_LENGTH) {
      setError("Enter the full verification code.");
      return;
    }
    submitCode(code);
  }

  // Auto-verify the instant all boxes are filled — no need to press "Verify"
  useEffect(() => {
    const code = digits.join("");
    if (code.length === CODE_LENGTH && !hasAutoSubmitted.current && !isSubmitting && !verified) {
      hasAutoSubmitted.current = true;
      submitCode(code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [digits]);

  async function handleResend() {
    setError("");
    setIsResending(true);
    try {
      await resendOtp({ email });
      startCooldown();
      setDigits(Array(CODE_LENGTH).fill(""));
      hasAutoSubmitted.current = false;
      inputRefs.current[0]?.focus();
    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setIsResending(false);
    }
  }

  return (
    <div className="verify-phone-page" dir={lang === "fa" ? "rtl" : "ltr"}>
      {verified ? (
        <p className="success-message">🎉 Registration complete! Redirecting...</p>
      ) : (
        <>
          <h2 className="verify-title">Verify your email address</h2>
          <p className="verify-subtitle">Enter the code sent to {email}</p>

          <form onSubmit={handleSubmit}>
            <div className={`otp-input-group ${shake ? "shake" : ""}`} onPaste={handlePaste}>
              {digits.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleDigitChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  className={`otp-digit-input ${error ? "otp-digit-input-error" : ""}`}
                  disabled={isSubmitting}
                  aria-label={`Digit ${index + 1} of verification code`}
                />
              ))}
            </div>

            {error && <p className="error-message">{error}</p>}

            <button type="submit" className="verify-button" disabled={isSubmitting}>
              {isSubmitting ? "Verifying..." : "Verify"}
            </button>
          </form>

          <button
            type="button"
            className="resend-button"
            onClick={handleResend}
            disabled={cooldown > 0 || isResending}
          >
            {cooldown > 0 ? `Resend code in ${cooldown}s` : isResending ? "Sending..." : "Resend code"}
          </button>
        </>
      )}
    </div>
  );
}